import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d66t6l6d937i0243n010', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-[#f9f9f9] py-24">
      <div className="max-w-[1400px] mx-auto px-8">
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-[48px] font-bold text-[#1a1a1a] mb-4">Get in Touch</h2>
          <p className="text-[16px] text-[#666] max-w-[600px] mx-auto">
            Have questions about our products or want to learn more about MIKRA d.o.o.? We'd love to hear from you.
          </p>
        </div>

        <div className="flex gap-16 items-start">
          {/* Contact Form */}
          <div className="w-[60%]">
            <form 
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit} 
              className="bg-white rounded-[20px] p-12 shadow-[0_8px_32px_rgba(0,0,0,0.06)]"
            >
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-[13px] font-medium text-[#333] mb-2 uppercase tracking-[1px]">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[#e0e0e0] rounded-lg text-[15px] focus:outline-none focus:border-[#d4ff00] transition-colors"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-[13px] font-medium text-[#333] mb-2 uppercase tracking-[1px]">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[#e0e0e0] rounded-lg text-[15px] focus:outline-none focus:border-[#d4ff00] transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="subject" className="block text-[13px] font-medium text-[#333] mb-2 uppercase tracking-[1px]">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#e0e0e0] rounded-lg text-[15px] focus:outline-none focus:border-[#d4ff00] transition-colors"
                  placeholder="How can we help?"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-[13px] font-medium text-[#333] mb-2 uppercase tracking-[1px]">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-[#e0e0e0] rounded-lg text-[15px] focus:outline-none focus:border-[#d4ff00] transition-colors resize-none"
                  placeholder="Tell us more about your inquiry..."
                ></textarea>
                <div className="text-right text-[12px] text-[#999] mt-1">
                  {formData.message.length}/500
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1a1a1a] text-white py-4 rounded-lg text-[15px] font-medium hover:bg-[#2a2a2a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-[#d4ff00]/20 border border-[#d4ff00] rounded-lg text-[14px] text-[#1a1a1a]">
                  Thank you! Your message has been sent successfully.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-[14px] text-red-600">
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
          </div>

          {/* Contact Information */}
          <div className="w-[40%] pt-8">
            <div className="space-y-8">
              <div>
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#1a1a1a] rounded-full flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-[13px] uppercase tracking-[1.2px] text-[#888] font-medium mb-2">Address</h3>
                    <p className="text-[15px] text-[#1a1a1a] leading-relaxed">
                      Bana Josipa Jelacica 25a<br />
                      Duga Resa, 47250<br />
                      Croatia
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#1a1a1a] rounded-full flex-shrink-0">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-[13px] uppercase tracking-[1.2px] text-[#888] font-medium mb-2">Email</h3>
                    <a href="mailto:support@mikradoo.com" className="text-[15px] text-[#1a1a1a] hover:text-[#d4ff00] transition-colors cursor-pointer">
                      support@mikradoo.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#1a1a1a] rounded-full flex-shrink-0">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-[13px] uppercase tracking-[1.2px] text-[#888] font-medium mb-2">Phone</h3>
                    <a href="tel:+13158608764" className="text-[15px] text-[#1a1a1a] hover:text-[#d4ff00] transition-colors cursor-pointer">
                      +1 3158608764
                    </a>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-[#e0e0e0]">
                <p className="text-[14px] text-[#666] leading-relaxed italic">
                  "We're committed to responding to all inquiries within 24 hours. Your privacy and security are our top priorities."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
